import { Controller } from "@hotwired/stimulus";
import { Turbo } from "@hotwired/turbo-rails";

// The visit schedule editor, drawn in the page. Its fields are the page's
// own, submitted with it; this only redraws its controls as they change.
export default class extends Controller {
  static targets = ["draft", "limitStatus"];

  static values = {
    draftUrl: String,
    editorId: String,
    fieldNamePrefix: String,
    bookingRequestId: String,
    timeZone: String
  };

  connect() {
    this.scrollErrorsIntoView();
  }

  preview() {
    this.clearLimitStatus();
    this.request(this.draftUrlValue);
  }

  datesChanged(event) {
    this.clearLimitStatus();
    this.request(this.draftUrlValue, {
      selected_dates_present: "1",
      selected_dates: event.detail.selectedDates || []
    });
  }

  dateLimitReached(event) {
    if (this.hasLimitStatusTarget) {
      this.limitStatusTarget.textContent = event.detail.message;
    }
  }

  async request(url, extraParams = {}) {
    const body = this.formData(extraParams);
    const response = await fetch(url, {
      method: "POST",
      headers: {
        Accept: "text/vnd.turbo-stream.html",
        "X-CSRF-Token": this.csrfToken()
      },
      body
    });

    const html = await response.text();
    if (html) Turbo.renderStreamMessage(html);
    return response;
  }

  formData(extraParams = {}) {
    const form = this.element.closest("form");
    const body = form ? new FormData(form) : new FormData();
    body.delete("_method");
    body.set("editor_id", this.editorIdValue);
    body.set("field_name_prefix", this.fieldNamePrefixValue);
    if (this.hasBookingRequestIdValue) {
      body.set("booking_request_id", this.bookingRequestIdValue);
    }
    if (this.hasTimeZoneValue) {
      body.set("time_zone", this.timeZoneValue);
    }
    Object.entries(extraParams).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        body.delete(`${key}[]`);
        value.forEach((item) => body.append(`${key}[]`, item));
      } else if (value !== undefined && value !== null) {
        body.set(key, value);
      }
    });
    return body;
  }

  // A failed submit draws the page again, with the step's errors in the
  // editor's alert. The first editor with errors brings them into view.
  scrollErrorsIntoView() {
    const alert = this.element.querySelector(`#${this.editorIdValue}_errors`);
    if (!alert || document.querySelector('.alert[id$="_errors"]') !== alert) return;

    requestAnimationFrame(() => {
      const { top, bottom } = alert.getBoundingClientRect();
      if (top < 0 || bottom > window.innerHeight) alert.scrollIntoView({ block: "center" });
    });
  }

  clearLimitStatus() {
    if (this.hasLimitStatusTarget) this.limitStatusTarget.textContent = "";
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
  }
}
